/**
 * Pure Mermaid flowchart parser.
 *
 * This parser intentionally stops at the data model: no layout, React Flow, or
 * renderer assumptions live here.
 */

import { parseHeaderDirection, sanitizeMermaidLine } from './flowchart-parser-edge-chain';
import { commitFlowchartLine } from './flowchart-parser-lines';
import type { ParsedFlowchart } from './flowchart-parser-model';
import {
  buildParsedFlowchart,
  createFlowchartParserState,
} from './flowchart-parser-state';

export {
  FlowchartDirection,
  FlowchartEdgeKind,
  FlowchartLabelOrigin,
  FlowchartMarkerKind,
  FlowchartNodeRole,
  FlowchartNodeShape,
} from './flowchart-parser-model';
export type {
  FlowchartEdge,
  FlowchartNode,
  FlowchartNodeStyle,
  FlowchartSubgraph,
  ParsedFlowchart,
} from './flowchart-parser-model';

const FLOWCHART_HEADER_PRESENT_REGEX = /^(?:flowchart|graph)\b/i;

function getSanitizedMermaidLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((line) => sanitizeMermaidLine(line))
    .filter((line) => line.length > 0);
}

export function parseMermaidFlowchart(raw: string): ParsedFlowchart | null {
  const lines = getSanitizedMermaidLines(raw);
  const headerIndex = lines.findIndex((line) => FLOWCHART_HEADER_PRESENT_REGEX.test(line));
  if (headerIndex === -1) {
    return null;
  }

  const direction = parseHeaderDirection(lines[headerIndex] ?? '');
  const state = createFlowchartParserState();
  lines
    .slice(headerIndex + 1)
    .forEach((line) => commitFlowchartLine({ line, state }));

  return buildParsedFlowchart({ direction, state });
}
